import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, RefreshCcw, Download, MessageCircle, GraduationCap, Brain, Heart, BookOpen, Activity, Flame } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const SCALE = [
    { v: 1, label: "Never" },
    { v: 2, label: "Rarely" },
    { v: 3, label: "Sometimes" },
    { v: 4, label: "Often" },
    { v: 5, label: "Always" },
];

const PILLAR_ICONS = {
    THOUGHTS: Brain,
    EMOTIONS: Heart,
    WORD: BookOpen,
    HABITS: Activity,
    HOPE: Flame,
};

const PILLAR_LABELS = {
    THOUGHTS: "Thoughts",
    EMOTIONS: "Emotions",
    WORD: "The Word",
    HABITS: "Habits",
    HOPE: "Hope",
};

const Quiz = () => {
    const [questions, setQuestions] = useState([]);
    const [step, setStep] = useState(0); // 0..questions.length-1 (q), then "email", then "result"
    const [answers, setAnswers] = useState({});
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [stage, setStage] = useState("intro"); // intro, questions, email, result
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/quiz/questions").then((r) => setQuestions(r.data)).catch(() => {});
    }, []);

    const current = questions[step];
    const progress = questions.length ? Math.round(((step + (stage === "questions" ? 0 : 1)) / questions.length) * 100) : 0;

    const select = (val) => {
        setAnswers({ ...answers, [current.id]: val });
        if (step + 1 < questions.length) {
            setTimeout(() => setStep(step + 1), 200);
        } else {
            setTimeout(() => setStage("email"), 200);
        }
    };

    const submit = async (skipEmail = false) => {
        setLoading(true);
        try {
            const payload = { answers };
            if (!skipEmail) {
                if (email) payload.email = email;
                if (name) payload.name = name;
            }
            const res = await api.post("/quiz/submit", payload);
            setResult(res.data.result);
            setStage("result");
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Couldn't score your assessment. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const restart = () => {
        setAnswers({});
        setStep(0);
        setResult(null);
        setEmail("");
        setName("");
        setStage("intro");
    };

    return (
        <div className="bg-black min-h-screen" data-testid="quiz-page">
            {/* HEADER */}
            <section className="border-b border-white/10 py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" />
                        Self-Assessment
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-[0.9]">
                        Battlefield <span className="text-red-600">Readiness</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-white/70 text-lg">
                        20 questions across 5 pillars — <span className="text-white">Thoughts · Emotions · Word · Habits · Hope</span>. Brutally honest with yourself = useful result. Takes ~3 minutes.
                    </p>
                </div>
            </section>

            <section className="py-16 md:py-24">
                <div className="max-w-3xl mx-auto px-6 lg:px-10">
                    {/* GREETING VIDEO — only shown on the intro stage */}
                    {stage === "intro" && (
                        <div className="mb-12" data-testid="quiz-greet-video-wrap">
                            <div className="flex items-center gap-3 mb-4 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                                <span className="h-px w-8 bg-red-600" />
                                Watch First
                            </div>
                            <div className="relative border-2 border-red-700/50 bg-black overflow-hidden shadow-[0_0_60px_-15px_rgba(185,28,28,0.5)] max-w-sm mx-auto">
                                <div className="relative aspect-[9/16] bg-[#050505]">
                                    <video
                                        src="/assessment-greet.mp4"
                                        poster="/assessment-greet-poster.jpg"
                                        className="w-full h-full object-cover"
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        controls
                                        preload="metadata"
                                        data-testid="quiz-greet-video"
                                    >
                                        Sorry, your browser can't play this video. Tap "Begin Assessment" below to get started.
                                    </video>
                                    <div className="absolute top-3 left-3 bg-red-700 px-3 py-1 font-stencil text-white text-[10px] tracking-widest pointer-events-none">
                                        WATCH
                                    </div>
                                </div>
                            </div>
                            <p className="mt-4 text-center text-white/50 text-sm">
                                A quick word from Tim before you start.
                            </p>
                        </div>
                    )}

                    {/* INTRO */}
                    {stage === "intro" && (
                        <div className="border border-white/15 bg-[#0a0a0a] p-10 md:p-14" data-testid="quiz-intro">
                            <div className="font-stencil tracking-widest text-red-600 text-xs">[ BRIEFING ]</div>
                            <h2 className="mt-4 font-display text-3xl md:text-5xl uppercase tracking-tight leading-[0.95]">
                                Where are you losing ground?
                            </h2>
                            <p className="mt-6 text-white/70 leading-relaxed">
                                You'll get an honest snapshot of your current battle-readiness across the five pillars that decide whether you win or lose the war in your mind:
                            </p>

                            <div className="mt-6 grid sm:grid-cols-5 gap-px bg-white/10 border border-white/10">
                                {["THOUGHTS", "EMOTIONS", "WORD", "HABITS", "HOPE"].map((p) => {
                                    const Icon = PILLAR_ICONS[p];
                                    return (
                                        <div key={p} className="bg-black p-4 text-center">
                                            <Icon size={20} className="text-red-600 mx-auto" strokeWidth={1.5} />
                                            <div className="mt-2 font-stencil text-[10px] tracking-widest text-white/70">{p}</div>
                                        </div>
                                    );
                                })}
                            </div>

                            <p className="mt-6 text-white/55 text-sm leading-relaxed">
                                At the end you'll get your scorecard, your weakest front, a deep-dive on each pillar, a verse to plant, and a clear next step. Takes about <span className="text-white">3 minutes</span>.
                            </p>
                            <button
                                onClick={() => setStage("questions")}
                                className="mt-10 btn-primary"
                                data-testid="quiz-start"
                            >
                                Begin Assessment <ArrowRight size={18} />
                            </button>
                        </div>
                    )}

                    {/* QUESTIONS */}
                    {stage === "questions" && current && (
                        <div data-testid={`quiz-q-${current.id}`}>
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    {(() => {
                                        const Icon = PILLAR_ICONS[current.pillar];
                                        return Icon ? <Icon size={16} className="text-red-600" strokeWidth={1.5} /> : null;
                                    })()}
                                    <div className="font-stencil tracking-widest text-xs text-red-600">
                                        {current.pillar} · {String(step + 1).padStart(2, "0")} / {String(questions.length).padStart(2, "0")}
                                    </div>
                                </div>
                                <div className="text-xs text-white/40">{progress}%</div>
                            </div>
                            <div className="h-1 bg-white/10 mb-10">
                                <div className="h-full bg-red-700 transition-all" style={{ width: `${progress}%` }} />
                            </div>

                            <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight leading-[0.95]">
                                {current.text}
                            </h2>

                            <div className="mt-12 grid gap-3">
                                {SCALE.map((s) => (
                                    <button
                                        key={s.v}
                                        onClick={() => select(s.v)}
                                        className={`text-left p-5 border transition-all flex items-center justify-between group ${
                                            answers[current.id] === s.v
                                                ? "border-red-600 bg-red-700/20"
                                                : "border-white/15 hover:border-white"
                                        }`}
                                        data-testid={`quiz-answer-${s.v}`}
                                    >
                                        <span className="font-display tracking-[0.2em] text-lg uppercase">{s.label}</span>
                                        <span className="font-stencil text-white/40 group-hover:text-red-600 text-sm">
                                            {s.v} / 5
                                        </span>
                                    </button>
                                ))}
                            </div>

                            {step > 0 && (
                                <button
                                    onClick={() => setStep(step - 1)}
                                    className="mt-8 inline-flex items-center gap-2 text-white/50 hover:text-white text-sm font-display tracking-[0.2em] uppercase"
                                    data-testid="quiz-back"
                                >
                                    <ArrowLeft size={14} /> Back
                                </button>
                            )}
                        </div>
                    )}

                    {/* EMAIL CAPTURE — REQUIRED */}
                    {stage === "email" && (
                        <div className="border border-white/15 bg-[#0a0a0a] p-10 md:p-14" data-testid="quiz-email">
                            <div className="font-stencil tracking-widest text-red-600 text-xs">[ ONE LAST STEP ]</div>
                            <h2 className="mt-4 font-display text-3xl md:text-5xl uppercase tracking-tight leading-[0.95]">
                                Where should we send your results?
                            </h2>
                            <p className="mt-6 text-white/70">
                                You'll get your full scorecard on screen — and a copy in your inbox so you can come back to it. We'll also check in once a week with one battle-ready prompt. <span className="text-white">No spam, ever.</span>
                            </p>

                            <div className="mt-8 space-y-3">
                                <input
                                    placeholder="First name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full px-5 py-4 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                                    data-testid="quiz-name"
                                    required
                                />
                                <input
                                    type="email"
                                    placeholder="your@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-5 py-4 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                                    data-testid="quiz-email-input"
                                    required
                                />
                            </div>

                            <div className="mt-8">
                                <button
                                    onClick={() => submit(false)}
                                    disabled={loading || !email.trim() || !name.trim()}
                                    className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="quiz-submit"
                                >
                                    {loading ? "Calculating..." : "See My Results"} <ArrowRight size={18} />
                                </button>
                            </div>
                            <p className="mt-4 text-white/40 text-xs">
                                We protect your inbox. Unsubscribe with one click anytime.
                            </p>
                        </div>
                    )}

                    {/* RESULT */}
                    {stage === "result" && result && (
                        <div data-testid="quiz-result">
                            <div className="text-center">
                                <div className="font-stencil tracking-widest text-red-600 text-xs">[ ASSESSMENT COMPLETE ]</div>
                                <div className="mt-4 font-display text-7xl md:text-9xl text-red-600 leading-none">
                                    {result.overall_percent}%
                                </div>
                                <h2 className="mt-4 font-display text-3xl md:text-5xl uppercase tracking-tight">
                                    {result.verdict}
                                </h2>
                                <p className="mt-6 max-w-xl mx-auto text-white/75 leading-relaxed">
                                    {result.message}
                                </p>
                            </div>

                            {/* ── 5-PILLAR BREAKDOWN ──────────────────────────────── */}
                            <div className="mt-12">
                                <div className="font-stencil text-xs tracking-widest text-red-600 mb-4">YOUR FIVE FRONTS</div>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10">
                                    {(result.pillar_order || ["THOUGHTS", "EMOTIONS", "WORD", "HABITS", "HOPE"]).map((p) => {
                                        const b = result.breakdown[p];
                                        if (!b) return null;
                                        const Icon = PILLAR_ICONS[p];
                                        const weakest = p === result.weakest_pillar;
                                        return (
                                            <div
                                                key={p}
                                                className={`p-6 ${weakest ? "bg-red-700/15" : "bg-black"} relative`}
                                                data-testid={`pillar-${p.toLowerCase()}`}
                                            >
                                                {Icon && <Icon size={18} className={weakest ? "text-red-500" : "text-red-600/80"} strokeWidth={1.5} />}
                                                <div className="mt-3 font-stencil text-[10px] tracking-widest text-white/70">{p}</div>
                                                <div className={`mt-2 font-display text-4xl md:text-5xl leading-none ${weakest ? "text-red-500" : "text-white"}`}>
                                                    {b.percent}%
                                                </div>
                                                <div className="mt-1 text-white/40 text-xs">{b.score} / {b.max}</div>
                                                {weakest && (
                                                    <div className="mt-3 text-[10px] font-stencil tracking-[0.2em] uppercase text-red-500">
                                                        ← Weakest
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* ── WEAKEST FRONT DIAGNOSIS ─────────────────────────── */}
                            {result.weakest_diagnosis && (
                                <div className="mt-10 border border-red-700/40 bg-gradient-to-br from-red-950/40 to-black p-8 md:p-10" data-testid="quiz-diagnosis">
                                    <div className="flex items-start gap-4">
                                        {(() => {
                                            const Icon = PILLAR_ICONS[result.weakest_pillar];
                                            return Icon ? <Icon size={28} className="text-red-500 flex-shrink-0 mt-1" strokeWidth={1.5} /> : null;
                                        })()}
                                        <div className="flex-1">
                                            <div className="font-stencil text-[10px] tracking-[0.3em] text-red-500">
                                                THE FRONT TO FOCUS ON
                                            </div>
                                            <h3 className="mt-2 font-display text-3xl md:text-4xl uppercase tracking-tight leading-[1]">
                                                {result.weakest_pillar_label || PILLAR_LABELS[result.weakest_pillar]}
                                            </h3>
                                            {result.weakest_tagline && (
                                                <p className="mt-1 text-red-400/80 text-sm font-display tracking-wide">
                                                    {result.weakest_tagline}
                                                </p>
                                            )}
                                            <p className="mt-4 text-white/80 leading-relaxed">
                                                {result.weakest_diagnosis}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── DETAILED PER-FRONT REPORTS ──────────────────────── */}
                            <div className="mt-16" data-testid="quiz-details">
                                <div className="font-stencil text-xs tracking-widest text-red-600 mb-4">YOUR FIVE FRONTS — IN DETAIL</div>
                                <div className="space-y-4">
                                    {(result.pillar_order || ["THOUGHTS", "EMOTIONS", "WORD", "HABITS", "HOPE"]).map((p) => {
                                        const b = result.breakdown[p];
                                        if (!b || !b.detail) return null;
                                        const Icon = PILLAR_ICONS[p];
                                        const isWeakest = p === result.weakest_pillar;
                                        const bandColor =
                                            b.band === "strong" ? "text-green-500" :
                                            b.band === "fair" ? "text-amber-400" :
                                            "text-red-500";
                                        const bandBorder =
                                            b.band === "strong" ? "border-green-700/30" :
                                            b.band === "fair" ? "border-amber-700/30" :
                                            "border-red-700/40";
                                        return (
                                            <div
                                                key={p}
                                                className={`border ${isWeakest ? "border-red-700/60 bg-gradient-to-br from-red-950/20 to-black" : `${bandBorder} bg-[#0a0a0a]`} p-6 md:p-8`}
                                                data-testid={`detail-${p.toLowerCase()}`}
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex items-start gap-4 flex-1 min-w-0">
                                                        {Icon && (
                                                            <div className={`p-2.5 border ${bandBorder} bg-black/40 flex-shrink-0`}>
                                                                <Icon size={20} className={bandColor} strokeWidth={1.5} />
                                                            </div>
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                                                                <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-tight">
                                                                    {b.label}
                                                                </h3>
                                                                {isWeakest && (
                                                                    <span className="font-stencil text-[10px] tracking-[0.3em] text-red-500">
                                                                        ← FOCUS HERE
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-red-400/80 text-sm font-display tracking-wide mt-0.5">
                                                                {b.tagline}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <div className={`font-display text-3xl md:text-4xl leading-none ${bandColor}`}>{b.percent}%</div>
                                                        <div className={`mt-1 font-stencil text-[9px] tracking-[0.2em] ${bandColor}`}>
                                                            {b.band_label}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mt-5 text-white/75 leading-relaxed whitespace-pre-line">
                                                    {b.detail}
                                                </div>

                                                {b.train_tip && (
                                                    <div className="mt-5 border-l-2 border-red-600 pl-4 py-2 bg-black/40">
                                                        <div className="font-stencil text-[10px] tracking-[0.3em] text-red-500 mb-1">
                                                            TRAIN THIS FRONT
                                                        </div>
                                                        <p className="text-white/80 text-sm leading-relaxed">{b.train_tip}</p>
                                                    </div>
                                                )}

                                                {b.scripture && (
                                                    <div className="mt-3 text-white/45 text-xs italic">
                                                        {b.scripture}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* ── PLANT THIS (verse) ──────────────────────────────── */}
                            <div className="mt-10 border-l-2 border-red-600 pl-6 py-4 bg-[#0a0a0a]">
                                <div className="font-stencil text-xs tracking-widest text-red-600">PLANT THIS</div>
                                <p className="mt-3 italic text-white/90 text-lg">{result.recommended_scripture}</p>
                            </div>

                            {/* ── LONG-TERM PATH ──────────────────────────────────── */}
                            <div className="mt-16" data-testid="quiz-next-steps">
                                <div className="font-stencil text-xs tracking-widest text-red-600 mb-4">YOUR NEXT THREE MOVES</div>
                                <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                                    <Link
                                        to="/free-tools"
                                        className="bg-black p-6 hover:bg-[#0a0a0a] group transition-colors"
                                        data-testid="quiz-next-freebie"
                                    >
                                        <Download size={22} className="text-red-600" strokeWidth={1.5} />
                                        <div className="mt-4 font-stencil text-[10px] tracking-[0.3em] text-white/50">STEP 01</div>
                                        <h4 className="mt-2 font-display text-xl uppercase tracking-tight leading-tight group-hover:text-red-600 transition-colors">
                                            Grab The Battle Drill
                                        </h4>
                                        <p className="mt-3 text-sm text-white/60 leading-relaxed">
                                            Free 4-step framework — Recognize · Reject · Replace · Repeat. The daily move that retrains your mind.
                                        </p>
                                    </Link>
                                    <Link
                                        to="/companion"
                                        className="bg-black p-6 hover:bg-[#0a0a0a] group transition-colors"
                                        data-testid="quiz-next-companion"
                                    >
                                        <MessageCircle size={22} className="text-red-600" strokeWidth={1.5} />
                                        <div className="mt-4 font-stencil text-[10px] tracking-[0.3em] text-white/50">STEP 02</div>
                                        <h4 className="mt-2 font-display text-xl uppercase tracking-tight leading-tight group-hover:text-red-600 transition-colors">
                                            Open The Companion
                                        </h4>
                                        <p className="mt-3 text-sm text-white/60 leading-relaxed">
                                            Tell it what's loudest tonight. Built for the lie hitting hardest, with Scripture + neuroscience in the same answer.
                                        </p>
                                    </Link>
                                    <Link
                                        to="/course"
                                        className="bg-black p-6 hover:bg-[#0a0a0a] group transition-colors"
                                        data-testid="quiz-next-course"
                                    >
                                        <GraduationCap size={22} className="text-red-600" strokeWidth={1.5} />
                                        <div className="mt-4 font-stencil text-[10px] tracking-[0.3em] text-white/50">STEP 03</div>
                                        <h4 className="mt-2 font-display text-xl uppercase tracking-tight leading-tight group-hover:text-red-600 transition-colors">
                                            MindWorks BattleFront
                                        </h4>
                                        <p className="mt-3 text-sm text-white/60 leading-relaxed">
                                            The full course — 5 modules, 21 lessons. The long game. Lock in early access.
                                        </p>
                                    </Link>
                                </div>
                            </div>

                            {/* ── HOW YOU GROW + HOW I CAN HELP (Personal note from Tim) ── */}
                            <div className="mt-20" data-testid="quiz-grow-help">
                                <div className="font-stencil text-xs tracking-widest text-red-600 mb-4">FROM TIM — A WORD BEFORE YOU GO</div>

                                <div className="border border-white/10 bg-[#0a0a0a] p-8 md:p-12">
                                    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
                                        {/* LEFT: How you grow */}
                                        <div className="lg:col-span-7">
                                            <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-[0.95]">
                                                How You <span className="text-red-600">Grow.</span>
                                            </h3>
                                            <p className="mt-6 text-white/80 leading-relaxed">
                                                A quiz score doesn't define you. It just shows you the front you've been ignoring.
                                            </p>
                                            <p className="mt-4 text-white/70 leading-relaxed">
                                                Growth doesn't come from one big breakthrough. It comes from a thousand tiny moments where you choose the harder, holier thought — and your brain quietly lays new track for the next one. That's neuroplasticity and renewing of the mind in the same sentence. <span className="text-white">Repetition is the chisel.</span>
                                            </p>
                                            <p className="mt-4 text-white/70 leading-relaxed">
                                                You won't fix all five fronts at once. You don't have to. Pick the weakest one. Show up for it daily for 30 days. Watch what happens to the other four.
                                            </p>
                                            <div className="mt-8 inline-flex items-center gap-3 px-4 py-2 bg-black border border-red-700/40">
                                                <span className="font-stencil text-[10px] tracking-[0.4em] text-red-600">ROMANS 12:2</span>
                                            </div>
                                        </div>

                                        {/* RIGHT: How I can help */}
                                        <div className="lg:col-span-5 lg:border-l lg:border-white/10 lg:pl-12">
                                            <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-[0.95]">
                                                How <span className="text-red-600">I</span> Help.
                                            </h3>
                                            <p className="mt-6 text-white/70 leading-relaxed">
                                                MindWorks isn't a one-time read. It's a daily rhythm built for the war in your mind.
                                            </p>
                                            <ul className="mt-6 space-y-4">
                                                <li className="flex items-start gap-3" data-testid="grow-bullet-drip">
                                                    <span className="font-stencil text-[10px] tracking-widest text-red-600 mt-1.5 flex-shrink-0">01</span>
                                                    <div>
                                                        <div className="font-display text-sm uppercase tracking-tight text-white">Weekly Emails</div>
                                                        <p className="text-white/60 text-sm mt-1 leading-relaxed">
                                                            Real talk from real life. One battle-tested prompt at a time — straight to your inbox.
                                                        </p>
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-3" data-testid="grow-bullet-tools">
                                                    <span className="font-stencil text-[10px] tracking-widest text-red-600 mt-1.5 flex-shrink-0">02</span>
                                                    <div>
                                                        <div className="font-display text-sm uppercase tracking-tight text-white">Free Tools & PDFs</div>
                                                        <p className="text-white/60 text-sm mt-1 leading-relaxed">
                                                            The Battle Drill PDF, articles, and frameworks. No paywall. Use them now.
                                                        </p>
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-3" data-testid="grow-bullet-companion">
                                                    <span className="font-stencil text-[10px] tracking-widest text-red-600 mt-1.5 flex-shrink-0">03</span>
                                                    <div>
                                                        <div className="font-display text-sm uppercase tracking-tight text-white">The Battlefield Companion</div>
                                                        <p className="text-white/60 text-sm mt-1 leading-relaxed">
                                                            On call when the lie hits at 2 AM. Scripture + neuroscience in one place.
                                                        </p>
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-3" data-testid="grow-bullet-course">
                                                    <span className="font-stencil text-[10px] tracking-widest text-red-600 mt-1.5 flex-shrink-0">04</span>
                                                    <div>
                                                        <div className="font-display text-sm uppercase tracking-tight text-white">MindWorks BattleFront</div>
                                                        <p className="text-white/60 text-sm mt-1 leading-relaxed">
                                                            The full course — 5 modules, 21 lessons. The long game, mapped out step-by-step.
                                                        </p>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    {/* Signature */}
                                    <div className="mt-12 pt-8 border-t border-white/10">
                                        <p className="text-white/75 leading-relaxed max-w-3xl">
                                            You're not too far gone. You're not the worst thing the lie has told you about yourself. The brain that has been at war can be at peace — not overnight, but repeatedly, deliberately, faithfully. <span className="text-white">Show up tomorrow. And the day after that.</span>
                                        </p>
                                        <div className="mt-6 flex items-center gap-3">
                                            <span className="font-stencil text-[10px] tracking-[0.4em] text-red-600">— TIM</span>
                                            <span className="h-px flex-1 bg-white/10 max-w-[120px]" />
                                            <span className="text-white/40 text-xs font-display tracking-[0.2em] uppercase">Founder · MindWorks</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-12 flex flex-wrap gap-3 justify-center">
                                <Link to="/companion" className="btn-primary" data-testid="quiz-cta-companion">
                                    Open the Companion <ArrowRight size={18} />
                                </Link>
                                <button onClick={restart} className="btn-ghost" data-testid="quiz-restart">
                                    <RefreshCcw size={16} /> Retake
                                </button>
                            </div>

                            {email && (
                                <div className="mt-12 text-center text-white/50 text-sm flex items-center justify-center gap-2">
                                    <CheckCircle2 size={14} className="text-green-500" />
                                    Results also sent to {email}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Quiz;
